import { faClock, faTriangleExclamation, faUsers } from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';

import type { IRaisersEdgeSyncSummary, TRaisersEdgeSyncSummaryField } from '@feathr/blackbox';
import type { IIntegrationUIStat } from '@feathr/components';
import { moment } from '@feathr/hooks';

/** Shown for a value that failed to load. */
export const UNAVAILABLE_VALUE = '—';

/** `null` means the request failed, so every value is unavailable. */
export type TRaisersEdgeSyncSummaryResult = IRaisersEdgeSyncSummary | null;

function isUnavailable(
  summary: TRaisersEdgeSyncSummaryResult,
  field: TRaisersEdgeSyncSummaryField,
): boolean {
  return !summary || summary.errors.includes(field);
}

function formatCount(
  summary: TRaisersEdgeSyncSummaryResult,
  field: 'constituents_synced' | 'mappings_need_attention',
): string {
  const count = summary?.[field];
  return isUnavailable(summary, field) || count === null || count === undefined
    ? UNAVAILABLE_VALUE
    : count.toLocaleString();
}

function formatLastSynced(summary: TRaisersEdgeSyncSummaryResult, t: TFunction): string {
  if (!summary || isUnavailable(summary, 'last_synced')) {
    return UNAVAILABLE_VALUE;
  }
  // The header shows the date, so show how fresh the data is here.
  return summary.last_synced ? moment.utc(summary.last_synced).fromNow() : t('Never');
}

/** The summary bar's stats, in display order. */
export function getSyncSummaryStats(
  summary: TRaisersEdgeSyncSummaryResult,
  t: TFunction,
): IIntegrationUIStat[] {
  const needAttention = summary?.mappings_need_attention ?? 0;
  return [
    {
      key: 'last_synced',
      label: t('Last checked'),
      value: formatLastSynced(summary, t),
      icon: faClock,
    },
    {
      key: 'constituents_synced',
      label: t('Constituents synced'),
      value: formatCount(summary, 'constituents_synced'),
      icon: faUsers,
    },
    {
      key: 'mappings_need_attention',
      label: t('Mappings need attention'),
      value: formatCount(summary, 'mappings_need_attention'),
      icon: faTriangleExclamation,
      attention: needAttention > 0,
    },
  ];
}

/**
 * The header's last sync time. The integration's own `date_last_down_synced` is never written for
 * Raiser's Edge, so prefer the summary once it has a value.
 */
export function getHeaderLastSyncedAt(
  summary: TRaisersEdgeSyncSummaryResult | undefined,
  fallback: string | undefined,
): string | null {
  if (summary && !summary.errors.includes('last_synced')) {
    return summary.last_synced;
  }
  return fallback ?? null;
}
